import { useState } from "react";
import { Check, Clock, Loader2, Package, PackageCheck, PackageSearch, Search, Truck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useShop } from "@/lib/shop";
import { fcfa, PAYMENT_LABELS, PAYMENT_STATUS_LABELS, STATUS_LABELS } from "@/lib/format";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/motion";

const STEPS = [
  { key: "en_attente", label: "En attente de vérification du paiement", icon: Clock },
  { key: "confirmee", label: "Confirmée", icon: PackageSearch },
  { key: "en_preparation", label: "En préparation", icon: Package },
  { key: "en_livraison", label: "En cours de livraison à Dakar", icon: Truck },
  { key: "livree", label: "Livrée", icon: PackageCheck },
];

const inputClass =
  "h-14 w-full rounded-md border border-clay bg-cream px-4 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none transition-colors duration-300";

const Tracking = () => {
  useSeo({
    title: "Suivi de commande",
    description: "Suivez votre commande Sama-Keur en temps réel : préparation, livraison à Dakar et partout au Sénégal, réception.",
    keywords: "suivi commande Dakar, livraison Sénégal, suivi colis Sama-Keur",
    path: "/suivi",
  });
  const [orderNumber, setOrderNumber] = useState("");
  const [phone, setPhone] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const shop = useShop();

  const search = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.get(`/orders/track/${encodeURIComponent(orderNumber.trim())}`, { params: { phone } });
      setOrder(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const currentStep = order ? Math.max(0, STEPS.findIndex((s) => s.key === order.status)) : -1;
  const cancelled = order?.status === "annulee";

  return (
    <main data-testid="tracking-page" className="">
      <div className="border-b border-clay">
        <div className="mx-auto max-w-7xl px-6 pb-12 pt-16 lg:px-10">
          <Reveal y={24}>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">Suivi</p>
            <h1 className="mt-3 font-display text-4xl font-bold tracking-tighter text-bark sm:text-5xl md:text-6xl">
              Où en est ma commande ?
            </h1>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 py-16 lg:px-10">
        <Reveal>
          <form onSubmit={search} className="grid gap-4 sm:grid-cols-2">
            <input
              data-testid="tracking-order-number-input"
              required
              placeholder="Numéro de commande (ex: SK-A1B2C3)"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              className={inputClass}
            />
            <input
              data-testid="tracking-phone-input"
              required
              placeholder="Téléphone utilisé à la commande"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={inputClass}
            />
            <button
              data-testid="tracking-search-button"
              disabled={loading}
              className="flex h-14 items-center justify-center gap-3 rounded-full bg-terracotta text-sm font-bold uppercase tracking-[0.15em] text-sand transition-colors duration-300 hover:bg-terracotta-dark disabled:opacity-50 sm:col-span-2"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Rechercher
            </button>
          </form>
          {error && (
            <p data-testid="tracking-error" className="mt-6 border border-destructive/30 bg-destructive/5 px-5 py-4 text-sm text-destructive">
              {error}
            </p>
          )}
        </Reveal>

        {order && (
          <Reveal delay={0.1}>
            <div data-testid="tracking-result" className="mt-14">
              <div className="border border-clay bg-cream p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Commande</p>
                    <p data-testid="tracking-order-number" className="mt-1 font-display text-3xl font-bold text-bark">{order.order_number}</p>
                    <p className="mt-1 text-sm text-bark/50">
                      Passée le {new Date(order.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-bark/50">Total — {PAYMENT_LABELS[order.payment_method]}</p>
                    {order.discount_percent > 0 && (
                      <p data-testid="tracking-discount" className="text-sm text-bark/45">
                        <span className="line-through">{fcfa(order.total + order.discount_amount)}</span>{" "}
                        <span className="font-bold text-terracotta">Réduction −{order.discount_percent}% : −{fcfa(order.discount_amount)}</span>
                      </p>
                    )}
                    <p data-testid="tracking-order-total" className="font-display text-2xl font-bold text-terracotta">{fcfa(order.total)}</p>
                    <span className={`mt-1 inline-block px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] ${order.payment_status === "payee" ? "bg-terracotta/10 text-terracotta" : "bg-ochre/15 text-ochre"}`}>
                      Paiement : {PAYMENT_STATUS_LABELS[order.payment_status] || order.payment_status}
                    </span>
                    {order.payment_plan === "two_times" && (
                      <p data-testid="tracking-two-times" className="mt-2 text-xs text-bark/60">
                        Acompte : <strong>{fcfa(order.deposit_amount)}</strong> — Reste à la livraison :{" "}
                        <strong className="text-terracotta">{fcfa(order.remaining_amount)}</strong>
                      </p>
                    )}
                  </div>
                </div>

                <ul className="mt-6 divide-y divide-clay border-t border-clay">
                  {order.items.map((i, idx) => (
                    <li key={idx} className="flex items-center gap-4 py-3">
                      <div className="h-14 w-12 shrink-0 overflow-hidden border border-clay">
                        <img src={i.image} alt={i.name} className="h-full w-full object-cover" />
                      </div>
                      <p className="flex-1 font-display text-base text-bark">{i.name}</p>
                      <p className="text-sm text-bark/50">×{i.quantity}</p>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-sm text-bark/50">
                  Livraison à domicile : {order.delivery_zone} — {order.customer.address}
                </p>
                {order.delivery_option && (
                  <p data-testid="tracking-delivery-option" className="mt-1 text-sm text-bark/50">
                    Mode de livraison : {order.delivery_option.name} ({order.delivery_option.delay}) — {fcfa(order.delivery_fee)}
                  </p>
                )}
                {(order.shipping_france_fee ?? 0) > 0 && (
                  <p data-testid="tracking-france-fee" className="mt-1 text-sm text-bark/50">
                    Frais de livraison depuis la France : {fcfa(order.shipping_france_fee)}
                  </p>
                )}

                {order.driver && order.status === "en_livraison" && (
                  <div data-testid="tracking-driver-card" className="mt-8 flex items-center gap-5 border border-terracotta/40 bg-sand p-6">
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-terracotta text-sand">
                      <Truck className="h-6 w-6" />
                    </span>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-terracotta">Votre livreur aujourd'hui</p>
                      <p data-testid="tracking-driver-name" className="mt-1 font-display text-2xl font-semibold text-bark">{order.driver.name}</p>
                      <a data-testid="tracking-driver-phone" href={`tel:${order.driver.phone.replace(/\s/g, "")}`} className="text-sm font-bold text-terracotta underline-offset-4 hover:underline">
                        {order.driver.phone}
                      </a>
                      <p className="mt-1 text-xs text-bark/40">Un SMS vous a été envoyé — appelez le livreur pour tout point de repère.</p>
                    </div>
                  </div>
                )}
              </div>

              {cancelled ? (
                <p data-testid="tracking-cancelled" className="mt-8 border border-destructive/30 bg-destructive/5 px-5 py-4 text-center text-sm font-semibold text-destructive">
                  Cette commande a été annulée. Contactez-nous au {shop.phone} pour plus d'informations.
                </p>
              ) : (
                <ol data-testid="tracking-timeline" className="mt-10 space-y-0">
                  {STEPS.map((step, i) => {
                    const done = i <= currentStep;
                    const current = i === currentStep;
                    const Icon = step.icon;
                    return (
                      <li key={step.key} data-testid={`tracking-step-${step.key}`} className="relative flex gap-5 pb-10 last:pb-0">
                        {i < STEPS.length - 1 && (
                          <span className={`absolute left-6 top-12 h-[calc(100%-3rem)] w-px ${i < currentStep ? "bg-terracotta" : "bg-clay"}`} />
                        )}
                        <span
                          className={`z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border transition-colors duration-500 ${
                            done ? "border-terracotta bg-terracotta text-sand" : "border-clay bg-cream text-bark/30"
                          }`}
                        >
                          {done && !current ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                        </span>
                        <div className="pt-2">
                          <p className={`font-display text-xl ${done ? "text-bark" : "text-bark/35"}`}>{step.label}</p>
                          {current && <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Étape en cours</p>}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </Reveal>
        )}
      </div>
    </main>
  );
};

export default Tracking;
